import { useEffect, useRef } from "react";

interface SwipeHandlers {
	onSwipeLeft?: () => void;
	onSwipeRight?: () => void;
	onSwipeUp?: () => void;
	onSwipeDown?: () => void;
}

interface SwipeOptions {
	threshold?: number; // Minimum distance in pixels to trigger swipe
	velocity?: number; // Minimum velocity to trigger swipe
}

/**
 * Hook to detect swipe gestures on an element
 */
export function useSwipeGesture(
	handlers: SwipeHandlers,
	options: SwipeOptions = {},
) {
	const { threshold = 50, velocity = 0.3 } = options;
	const elementRef = useRef<HTMLElement | null>(null);
	const touchStartRef = useRef<{ x: number; y: number; time: number } | null>(
		null,
	);

	useEffect(() => {
		const element = elementRef.current;
		if (!element) return;

		const handleTouchStart = (e: TouchEvent) => {
			const touch = e.touches[0];
			touchStartRef.current = {
				x: touch.clientX,
				y: touch.clientY,
				time: Date.now(),
			};
		};

		const handleTouchEnd = (e: TouchEvent) => {
			if (!touchStartRef.current) return;

			const touch = e.changedTouches[0];
			const deltaX = touch.clientX - touchStartRef.current.x;
			const deltaY = touch.clientY - touchStartRef.current.y;
			const deltaTime = Date.now() - touchStartRef.current.time;

			const absDeltaX = Math.abs(deltaX);
			const absDeltaY = Math.abs(deltaY);
			const velocityX = absDeltaX / deltaTime;
			const velocityY = absDeltaY / deltaTime;

			// Determine primary direction
			if (absDeltaX > absDeltaY) {
				// Horizontal swipe
				if (absDeltaX > threshold && velocityX > velocity) {
					if (deltaX > 0 && handlers.onSwipeRight) {
						handlers.onSwipeRight();
					} else if (deltaX < 0 && handlers.onSwipeLeft) {
						handlers.onSwipeLeft();
					}
				}
			} else {
				// Vertical swipe
				if (absDeltaY > threshold && velocityY > velocity) {
					if (deltaY > 0 && handlers.onSwipeDown) {
						handlers.onSwipeDown();
					} else if (deltaY < 0 && handlers.onSwipeUp) {
						handlers.onSwipeUp();
					}
				}
			}

			touchStartRef.current = null;
		};

		element.addEventListener("touchstart", handleTouchStart, { passive: true });
		element.addEventListener("touchend", handleTouchEnd, { passive: true });

		return () => {
			element.removeEventListener("touchstart", handleTouchStart);
			element.removeEventListener("touchend", handleTouchEnd);
		};
	}, [handlers, threshold, velocity]);

	return elementRef;
}
